import { accentStyleOf } from './accents';
import { z } from 'zod';
import { directImageUrl } from './image-url';
import { docMotionSchema, MUSIC_PART_PREFIX } from './motion/schema';
import {
  colorSchema,
  FONT_STYLES,
  IMAGE_FITS,
  QR_DOT_STYLES,
  QR_EYE_STYLES,
  STACK_DIRECTIONS,
  TEXT_ALIGNS,
  TEXT_TRANSFORMS,
  VERTICAL_ALIGNS,
  type Layer,
  type LayerType,
  type TemplateDoc,
} from './template';

/**
 * Modifications are how API users, CSV rows and signed URLs change a template:
 *
 *   { "title": { "text": "Summer sale" }, "photo": "https://…/a.jpg", "badge": { "hide": true } }
 *
 * Keys are layer names. Values are either an object of properties (snake_case or camelCase),
 * a scalar shorthand for the layer's primary property, or `null` to hide the layer.
 */

export class ModificationError extends Error {
  constructor(
    message: string,
    public readonly layer: string,
    public readonly property?: string,
  ) {
    super(message);
    this.name = 'ModificationError';
  }
}

export type ModificationValue = string | number | boolean | null;
export type LayerModification = Record<string, ModificationValue>;
export type Modifications = Record<string, LayerModification | ModificationValue>;

export const modificationsInputSchema = z.union([
  z.record(
    z.string(),
    z.union([
      z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
      z.string(),
      z.number(),
      z.boolean(),
      z.null(),
    ]),
  ),
  z.array(z.looseObject({ name: z.string() })),
]);

const PRIMARY_PROPERTY: Record<LayerType, string> = {
  text: 'text',
  image: 'image',
  shape: 'fill',
  qr: 'value',
  group: 'fill',
};

export function primaryProperty(type: LayerType): string {
  return PRIMARY_PROPERTY[type];
}

// ---------- value coercion ----------

function toNumber(
  v: ModificationValue,
  layer: string,
  prop: string,
  min = -1e6,
  max = 1e6,
): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  if (!Number.isFinite(n)) throw new ModificationError(`"${prop}" must be a number`, layer, prop);
  if (n < min || n > max) {
    throw new ModificationError(`"${prop}" must be between ${min} and ${max}`, layer, prop);
  }
  return n;
}

function toBool(v: ModificationValue, layer: string, prop: string): boolean {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (['true', '1', 'yes', 'y', 'on', 'да'].includes(s)) return true;
    if (['false', '0', 'no', 'n', 'off', 'нет', ''].includes(s)) return false;
  }
  throw new ModificationError(`"${prop}" must be a boolean`, layer, prop);
}

function toStr(v: ModificationValue, layer: string, prop: string, max = 20000): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  if (s.length > max)
    throw new ModificationError(`"${prop}" is longer than ${max} characters`, layer, prop);
  return s;
}

function toColor(v: ModificationValue, layer: string, prop: string): string {
  const s = toStr(v, layer, prop, 64).trim();
  const withHash = /^[0-9a-fA-F]{6}$|^[0-9a-fA-F]{8}$|^[0-9a-fA-F]{3}$/.test(s) ? `#${s}` : s;
  const parsed = colorSchema.safeParse(withHash);
  if (!parsed.success)
    throw new ModificationError(`"${prop}" must be a color like #FF0000`, layer, prop);
  return parsed.data;
}

function toEnum<T extends readonly string[]>(
  values: T,
  v: ModificationValue,
  layer: string,
  prop: string,
): T[number] {
  const s = toStr(v, layer, prop, 64).trim().toLowerCase();
  if (!(values as readonly string[]).includes(s)) {
    throw new ModificationError(`"${prop}" must be one of: ${values.join(', ')}`, layer, prop);
  }
  return s as T[number];
}

function toUrl(v: ModificationValue, layer: string, prop: string): string {
  const s = toStr(v, layer, prop, 4096).trim();
  if (s === '') return '';
  if (s.startsWith('/files/')) return s;
  if (/^data:image\/(png|jpe?g|webp|gif|svg\+xml);base64,[A-Za-z0-9+/=]+$/.test(s)) {
    if (s.length > 4096)
      throw new ModificationError(
        `"${prop}" data URLs must be small; upload the image instead`,
        layer,
        prop,
      );
    return s;
  }
  let url: URL;
  try {
    url = new URL(s);
  } catch {
    throw new ModificationError(`"${prop}" must be an http(s) URL`, layer, prop);
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    throw new ModificationError(`"${prop}" must be an http(s) URL`, layer, prop);
  }
  return directImageUrl(url.toString());
}

/** Clip files by their extension (MP4, MOV, WebM): an image layer plays them as video. */
const VIDEO_URL = /\.(mp4|m4v|mov|webm)(?:$|[?#])/i;
export function isVideoUrl(url: string): boolean {
  return VIDEO_URL.test(url);
}

/**
 * Put a clip in an image layer (the picture stays its poster frame). The length of a clip from
 * the API is not known here: 0 means "measure it at render time".
 */
function setClip(l: any, url: string) {
  if (!url) {
    l.video = null;
    return;
  }
  const prev = l.video;
  l.video = {
    src: url,
    duration: 0,
    trimStart: 0,
    trimEnd: null,
    speed: prev?.speed ?? 1,
    loop: prev?.loop ?? true,
    muted: prev?.muted ?? true,
    volume: prev?.volume ?? 1,
  };
}

function clipOf(l: any, layer: string, prop: string) {
  if (!l.video)
    throw new ModificationError(
      `"${prop}" needs a video: set "video" to a clip URL first`,
      layer,
      prop,
    );
  return l.video;
}

// ---------- property tables ----------

type Setter = (layer: any, value: ModificationValue, name: string, prop: string) => void;

const COMMON: Record<string, Setter> = {
  hide: (l, v, n, p) => (l.visible = !toBool(v, n, p)),
  visible: (l, v, n, p) => (l.visible = toBool(v, n, p)),
  x: (l, v, n, p) => (l.x = toNumber(v, n, p, -100000, 100000)),
  y: (l, v, n, p) => (l.y = toNumber(v, n, p, -100000, 100000)),
  width: (l, v, n, p) => (l.width = toNumber(v, n, p, 1, 100000)),
  height: (l, v, n, p) => (l.height = toNumber(v, n, p, 1, 100000)),
  rotation: (l, v, n, p) => (l.rotation = toNumber(v, n, p, -360, 360)),
  opacity: (l, v, n, p) => (l.opacity = toNumber(v, n, p, 0, 1)),
};

const TEXT: Record<string, Setter> = {
  text: (l, v, n, p) => (l.text = toStr(v, n, p)),
  color: (l, v, n, p) => (l.color = toColor(v, n, p)),
  font_family: (l, v, n, p) => (l.fontFamily = toStr(v, n, p, 128).trim() || l.fontFamily),
  font_size: (l, v, n, p) => (l.fontSize = toNumber(v, n, p, 1, 4000)),
  font_weight: (l, v, n, p) => (l.fontWeight = Math.round(toNumber(v, n, p, 100, 1000))),
  font_style: (l, v, n, p) => (l.fontStyle = toEnum(FONT_STYLES, v, n, p)),
  line_height: (l, v, n, p) => (l.lineHeight = toNumber(v, n, p, 0.3, 10)),
  letter_spacing: (l, v, n, p) => (l.letterSpacing = toNumber(v, n, p, -200, 2000)),
  align: (l, v, n, p) => (l.align = toEnum(TEXT_ALIGNS, v, n, p)),
  vertical_align: (l, v, n, p) => (l.verticalAlign = toEnum(VERTICAL_ALIGNS, v, n, p)),
  text_transform: (l, v, n, p) => (l.textTransform = toEnum(TEXT_TRANSFORMS, v, n, p)),
  auto_fit: (l, v, n, p) => (l.autoFit = toBool(v, n, p)),
  min_font_size: (l, v, n, p) => (l.minFontSize = toNumber(v, n, p, 1, 4000)),
  max_lines: (l, v, n, p) => (l.maxLines = Math.round(toNumber(v, n, p, 0, 1000))),
  background_color: (l, v, n, p) =>
    (l.background = v === null || v === '' ? null : { type: 'solid', color: toColor(v, n, p) }),
  stroke_color: (l, v, n, p) => (l.strokeColor = toColor(v, n, p)),
  stroke_width: (l, v, n, p) => (l.strokeWidth = toNumber(v, n, p, 0, 200)),
  border_radius: (l, v, n, p) => (l.borderRadius = toNumber(v, n, p, 0, 10000)),
  // Words of this text to accent ("тариф", several split by ";"), in the layer's accent style
  accent: (l, v, n, p) => {
    const style = accentStyleOf(l);
    const words = toStr(v, n, p, 2000)
      .split(';')
      .map((w) => w.trim())
      .filter(Boolean)
      .slice(0, 20);
    l.marks = [
      ...(l.marks ?? []),
      ...words.map((text) => ({ ...style, text: text.slice(0, 500) })),
    ];
  },
  accent_color: (l, v, n, p) => {
    const color = toColor(v, n, p);
    l.accent = { ...accentStyleOf(l), color };
    // Words picked in the editor take the new colour too: one accent colour per row
    l.marks = l.marks?.map((m: Record<string, unknown>) => ({ ...m, color }));
  },
  accent_numbers: (l, v, n, p) => (l.accentNumbers = toBool(v, n, p)),
};

const IMAGE: Record<string, Setter> = {
  // A clip URL makes the layer play that video; a picture URL replaces the picture (and a clip)
  image: (l, v, n, p) => {
    const url = toUrl(v, n, p);
    if (url && isVideoUrl(url)) setClip(l, url);
    else {
      l.src = url;
      l.video = null;
    }
  },
  video: (l, v, n, p) => setClip(l, toUrl(v, n, p)),
  /** Seconds into the clip where it starts / ends. */
  video_start: (l, v, n, p) => (clipOf(l, n, p).trimStart = toNumber(v, n, p, 0, 3600) * 1000),
  video_end: (l, v, n, p) =>
    (clipOf(l, n, p).trimEnd =
      v === null || v === '' ? null : toNumber(v, n, p, 0.05, 3600) * 1000),
  video_speed: (l, v, n, p) => (clipOf(l, n, p).speed = toNumber(v, n, p, 0.25, 4)),
  video_volume: (l, v, n, p) => (clipOf(l, n, p).volume = toNumber(v, n, p, 0, 1)),
  video_muted: (l, v, n, p) => (clipOf(l, n, p).muted = toBool(v, n, p)),
  video_loop: (l, v, n, p) => (clipOf(l, n, p).loop = toBool(v, n, p)),
  fit: (l, v, n, p) => (l.fit = toEnum(IMAGE_FITS, v, n, p)),
  focus_x: (l, v, n, p) => (l.focusX = toNumber(v, n, p, 0, 1)),
  focus_y: (l, v, n, p) => (l.focusY = toNumber(v, n, p, 0, 1)),
  border_radius: (l, v, n, p) => (l.borderRadius = toNumber(v, n, p, 0, 10000)),
  border_width: (l, v, n, p) => (l.borderWidth = toNumber(v, n, p, 0, 1000)),
  border_color: (l, v, n, p) => (l.borderColor = toColor(v, n, p)),
  grayscale: (l, v, n, p) => (l.grayscale = toBool(v, n, p)),
  blur: (l, v, n, p) => (l.blur = toNumber(v, n, p, 0, 200)),
  brightness: (l, v, n, p) => (l.brightness = toNumber(v, n, p, 0, 3)),
};

const SHAPE: Record<string, Setter> = {
  fill: (l, v, n, p) =>
    (l.fill = v === null || v === '' ? null : { type: 'solid', color: toColor(v, n, p) }),
  // "color" is what people try first for a shape
  color: (l, v, n, p) =>
    (l.fill = v === null || v === '' ? null : { type: 'solid', color: toColor(v, n, p) }),
  stroke_color: (l, v, n, p) => (l.strokeColor = toColor(v, n, p)),
  stroke_width: (l, v, n, p) => (l.strokeWidth = toNumber(v, n, p, 0, 1000)),
  border_radius: (l, v, n, p) => (l.borderRadius = toNumber(v, n, p, 0, 10000)),
};

const QR: Record<string, Setter> = {
  value: (l, v, n, p) => (l.value = toStr(v, n, p, 2000)),
  logo: (l, v, n, p) => (l.logo = toUrl(v, n, p)),
  logo_size: (l, v, n, p) => (l.logoSize = toNumber(v, n, p, 0.1, 0.3)),
  eye_color: (l, v, n, p) => (l.eyeColor = v === null || v === '' ? null : toColor(v, n, p)),
  dot_style: (l, v, n, p) => (l.dotStyle = toEnum(QR_DOT_STYLES, v, n, p)),
  eye_style: (l, v, n, p) => (l.eyeStyle = toEnum(QR_EYE_STYLES, v, n, p)),
  color: (l, v, n, p) => (l.foreground = toColor(v, n, p)),
  background_color: (l, v, n, p) =>
    (l.background = v === null || v === '' ? null : toColor(v, n, p)),
};

const GROUP: Record<string, Setter> = {
  fill: (l, v, n, p) =>
    (l.fill = v === null || v === '' ? null : { type: 'solid', color: toColor(v, n, p) }),
  color: (l, v, n, p) =>
    (l.fill = v === null || v === '' ? null : { type: 'solid', color: toColor(v, n, p) }),
  direction: (l, v, n, p) => (l.direction = toEnum(STACK_DIRECTIONS, v, n, p)),
  gap: (l, v, n, p) => (l.gap = toNumber(v, n, p, 0, 10000)),
  border_radius: (l, v, n, p) => (l.borderRadius = toNumber(v, n, p, 0, 10000)),
};

const TABLES: Record<LayerType, Record<string, Setter>> = {
  text: TEXT,
  image: IMAGE,
  shape: SHAPE,
  qr: QR,
  group: GROUP,
};

const ALIASES: Record<string, string> = {
  image_url: 'image',
  video_url: 'video',
  clip: 'video',
  src: 'image',
  url: 'image',
  fill_color: 'fill',
  background: 'background_color',
  hidden: 'hide',
  font: 'font_family',
  size: 'font_size',
  weight: 'font_weight',
  text_color: 'color',
  qr_value: 'value',
  logo_url: 'logo',
  angle: 'rotation',
};

/** camelCase / kebab-case → snake_case */
export function toSnakeCase(key: string): string {
  return key
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[-\s]+/g, '_')
    .toLowerCase();
}

/** Marker key meaning "the primary property of this layer" (used by flat sources). */
export const PRIMARY_KEY = '$value';

function resolveProperty(type: LayerType, rawKey: string): Setter | undefined {
  if (rawKey === PRIMARY_KEY) rawKey = PRIMARY_PROPERTY[type];
  let key = toSnakeCase(rawKey);
  const table = TABLES[type];
  if (!table[key] && !COMMON[key]) {
    if (type === 'qr' && (key === 'text' || key === 'url')) key = 'value';
    else key = ALIASES[key] ?? key;
  }
  return table[key] ?? COMMON[key];
}

/** List of modifiable properties per layer type (for docs and the UI). */
export function modifiableProperties(type: LayerType | 'audio'): string[] {
  if (type === 'audio') return ['url', 'volume', 'start', 'trim_start', 'fade_in', 'fade_out'];
  return [...Object.keys(TABLES[type]), ...Object.keys(COMMON)];
}

/** Accepts the map form or the array form `[{ name, ...props }]` and returns the map form. */
export function normalizeModifications(input: unknown): Modifications {
  if (input === null || input === undefined) return {};
  if (Array.isArray(input)) {
    const out: Modifications = {};
    for (const item of input) {
      if (!item || typeof item !== 'object' || typeof (item as any).name !== 'string') {
        throw new ModificationError('Each item of the layers array must have a "name"', '?');
      }
      const { name, ...rest } = item as Record<string, ModificationValue>;
      out[String(name)] = rest as LayerModification;
    }
    return out;
  }
  if (typeof input === 'object') return input as Modifications;
  throw new ModificationError('"layers" must be an object keyed by layer name', '?');
}

/**
 * Sounds of a video by reserved names: `"@music"` (the music) and `"@voice"` (a voice-over).
 * The value is a file URL, `null` to remove it, or `{ url, volume, start, trim_start, fade_in,
 * fade_out }` (times in seconds).
 */
export const SOUND_FIELDS = ['@music', '@voice'] as const;
export type SoundField = (typeof SOUND_FIELDS)[number];

interface SoundInput {
  src: string;
  volume?: number;
  start?: number;
  trimStart?: number;
  length?: number;
  fadeIn?: number;
  fadeOut?: number;
}

function soundInput(value: LayerModification | ModificationValue, name: string): SoundInput | null {
  if (value === null || value === false || value === '') return null;
  if (typeof value !== 'object') return { src: toUrl(value, name, 'url') };
  const out: SoundInput = { src: '' };
  for (const [raw, v] of Object.entries(value)) {
    const key = raw === PRIMARY_KEY ? 'url' : toSnakeCase(raw);
    if (key === 'url' || key === 'src') out.src = toUrl(v, name, key);
    else if (key === 'volume') out.volume = toNumber(v, name, key, 0, 1);
    else if (key === 'start') out.start = toNumber(v, name, key, 0, 60) * 1000;
    else if (key === 'trim_start') out.trimStart = toNumber(v, name, key, 0, 3600) * 1000;
    else if (key === 'length') out.length = toNumber(v, name, key, 0.05, 3600) * 1000;
    else if (key === 'fade_in') out.fadeIn = toNumber(v, name, key, 0, 10) * 1000;
    else if (key === 'fade_out') out.fadeOut = toNumber(v, name, key, 0, 10) * 1000;
    else throw new ModificationError(`Property "${raw}" is not supported for "${name}"`, name, raw);
  }
  if (!out.src) throw new ModificationError(`"${name}" needs a "url"`, name, 'url');
  return out;
}

/** Put music or a voice-over into a copy of the document's motion (0 length: measured later). */
function applySound(
  doc: TemplateDoc,
  field: SoundField,
  value: LayerModification | ModificationValue,
): TemplateDoc['motion'] {
  const sound = soundInput(value, field);
  const motion = structuredClone(doc.motion ?? docMotionSchema.parse({}));
  const name = sound?.src.split(/[?#]/)[0].split('/').pop()?.slice(0, 200);
  if (field === '@music') {
    motion.audio = sound
      ? {
          src: sound.src,
          name,
          duration: 0,
          trimStart: sound.trimStart ?? 0,
          start: sound.start ?? 0,
          length: sound.length ?? null,
          volume: sound.volume ?? 1,
          fadeIn: sound.fadeIn ?? 0,
          fadeOut: sound.fadeOut ?? 1000,
          bpm: null,
          beatOffset: 0,
          sync: false,
        }
      : null;
    // Parts cut off the old music go with it
    motion.tracks = (motion.tracks ?? []).filter((t) => !t.id.startsWith(MUSIC_PART_PREFIX));
  } else {
    motion.tracks = (motion.tracks ?? []).filter((t) => t.kind !== 'voice');
    if (sound)
      motion.tracks.push({
        id: 'api-voice',
        kind: 'voice',
        src: sound.src,
        name,
        duration: 0,
        start: sound.start ?? 0,
        trimStart: sound.trimStart ?? 0,
        length: sound.length ?? null,
        volume: sound.volume ?? 1,
        fadeIn: sound.fadeIn ?? 0,
        fadeOut: sound.fadeOut ?? 0,
      });
  }
  return motion;
}

export interface ApplyResult {
  doc: TemplateDoc;
  warnings: string[];
}

/** Apply modifications to a template document. Never mutates the input. */
export function applyModifications(doc: TemplateDoc, input: unknown): ApplyResult {
  const mods = normalizeModifications(input);
  const warnings: string[] = [];
  const layers: Layer[] = doc.layers.map((l) => structuredClone(l));
  const byName = new Map(layers.map((l) => [l.name, l]));
  let motion = doc.motion;

  for (const [name, mod] of Object.entries(mods)) {
    if ((SOUND_FIELDS as readonly string[]).includes(name) && !byName.has(name)) {
      motion = applySound({ ...doc, motion }, name as SoundField, mod);
      continue;
    }
    const layer = byName.get(name);
    if (!layer) {
      warnings.push(`Unknown layer "${name}" was ignored`);
      continue;
    }
    if (mod === null || mod === false) {
      layer.visible = false;
      continue;
    }
    if (typeof mod !== 'object') {
      const setter = resolveProperty(layer.type, PRIMARY_PROPERTY[layer.type]);
      setter!(layer, mod, name, PRIMARY_PROPERTY[layer.type]);
      continue;
    }
    for (const [prop, value] of Object.entries(mod)) {
      const setter = resolveProperty(layer.type, prop);
      if (!setter) {
        warnings.push(`Property "${prop}" is not supported for ${layer.type} layer "${name}"`);
        continue;
      }
      if (value === undefined) continue;
      setter(layer, value as ModificationValue, name, prop);
    }
  }
  return { doc: { ...doc, layers, ...(motion !== doc.motion ? { motion } : {}) }, warnings };
}

/**
 * Parse flat key/value pairs (CSV row, URL query string) into modifications.
 * `title=Hello` → { title: "Hello" };  `title.color=#f00` → { title: { color: "#f00" } }.
 * When both forms are used for one layer the scalar goes to the layer's primary property.
 */
export function modificationsFromFlat(entries: Iterable<[string, string]>): Modifications {
  const props: Record<string, LayerModification> = {};
  const scalars: Record<string, string> = {};
  for (const [rawKey, value] of entries) {
    const key = rawKey.trim();
    if (!key) continue;
    const dot = key.indexOf('.');
    if (dot === -1) scalars[key] = value;
    else (props[key.slice(0, dot)] ??= {})[key.slice(dot + 1)] = value;
  }
  const result: Modifications = { ...props };
  for (const [layer, value] of Object.entries(scalars)) {
    result[layer] = props[layer] ? { [PRIMARY_KEY]: value, ...props[layer] } : value;
  }
  return result;
}

/** Describes the dynamic fields of a template — used by docs, automations and the API. */
export interface TemplateField {
  /** Layer name, or a sound of the video (`@music`, `@voice`). */
  layer: string;
  type: LayerType | 'audio';
  /** Image layers playing a clip: the clip URL is the value. */
  media?: 'video';
  primary: string;
  defaultValue: string;
  properties: string[];
  description?: string;
  /** Locked in the editor: automations leave it as designed. */
  locked?: boolean;
}

export function describeFields(doc: TemplateDoc): TemplateField[] {
  // Stacks are structure, not content: keep them out of field lists (still modifiable by name)
  const fields: TemplateField[] = doc.layers
    .filter((l) => l.type !== 'group')
    .map((l) => ({
      ...(l.type === 'image' && l.video?.src ? { media: 'video' as const } : {}),
      layer: l.name,
      type: l.type,
      primary: PRIMARY_PROPERTY[l.type],
      defaultValue:
        l.type === 'text'
          ? l.text
          : l.type === 'image'
            ? (l.video?.src ?? l.src)
            : l.type === 'qr'
              ? l.value
              : l.fill?.type === 'solid'
                ? l.fill.color
                : '',
      properties: modifiableProperties(l.type),
      description: l.description,
      ...(l.locked ? { locked: true } : {}),
    }));
  // Animations and videos: their music and voice-over can be replaced too
  if (doc.motion) {
    const names = new Set(doc.layers.map((l) => l.name));
    const voice = doc.motion.tracks?.find((t) => t.kind === 'voice');
    const sounds: Array<[SoundField, string]> = [
      ['@music', doc.motion.audio?.src ?? ''],
      ['@voice', voice?.src ?? ''],
    ];
    for (const [field, value] of sounds)
      if (!names.has(field))
        fields.push({
          layer: field,
          type: 'audio',
          primary: 'url',
          defaultValue: value,
          properties: modifiableProperties('audio'),
        });
  }
  return fields;
}
